/**
 * Real project photos per project slug.
 *
 * File names are descriptive long-tail phrases (what the piece is + finish + room) for
 * Google Images; alt text is one natural sentence of what is visible — the main signal
 * for image search and what AI assistants read. City/locality goes in alt text, not
 * file names (avoids keyword stuffing across dozens of files).
 *
 * gallery: first 5 fill the editorial slots on the Full Project tab; moreImages go to
 * "More from this project". 3D pairs are named <desc>-3d-render / <desc>-as-built.
 * A tab with no photos (e.g. no before/after shots yet) is hidden on the page.
 * A photo reused across tabs points at one file, so it is stored and downloaded once.
 */

export type PhotoOrientation = 'landscape' | 'portrait' | 'square';

export interface ProjectPhoto {
  src: string;
  alt: string;
  orientation: PhotoOrientation;
  caption?: string;
}

export interface ProjectPhotoSet {
  hero: ProjectPhoto;
  gallery: ProjectPhoto[];
  moreImages: ProjectPhoto[];
  comparisons?: { label: string; render: ProjectPhoto; actual: ProjectPhoto }[];
  beforeAfter?: { label: string; before: ProjectPhoto; after: ProjectPhoto }[];
}

const dir = (project: string, sub: string) => `/assets/images/projects/${project}/${sub}`;

// ─── PROJECT 1 ───────────────────────────────────────────────────────────────

const p1 = dir('project-1', 'gallery');
const p1c = dir('project-1', '3d-vs-actual');
const GG = 'Gubbi Gudu villa, Anjanapura, Bangalore';
const ph = (src: string, alt: string, orientation: ProjectPhoto['orientation'], caption?: string): ProjectPhoto => ({ src, alt, orientation, caption });

// Gubbi Gudu — actual photos (photoshoot by Agith & Ravi + site photos)
const g1Living = ph(`${p1}/floating-walnut-tv-unit-with-fluted-wood-wall-panel.jpg`, `${GG} — living room with a floating walnut TV unit, angled white drawer fronts and a full-height fluted wood wall panel`, 'landscape', 'Living room — floating TV unit and fluted wood panel');
const g1LivingTall = ph('/assets/images/projects/project-1/gubbi-gudu-villa-living-room-with-walnut-tv-unit-and-fluted-wood-wall.jpg', `${GG} — living room with walnut TV unit, fluted wood wall and sheer full-height curtains`, 'portrait', 'Living room — fluted walnut wall');
const g1TvWide = ph(`${p1}/living-room-walnut-tv-unit-and-fluted-panel-wide-view.jpg`, `${GG} — walnut TV unit with angled white fronts beside the fluted wood rafters`, 'landscape', 'TV wall — strong without being heavy');
const g1Curtains = ph(`${p1}/living-room-with-full-height-curtains-and-recliners.jpg`, `${GG} — living room with full-height curtains, large windows and recliners`, 'portrait', 'Full-height curtains and large windows');
const g1Exterior = ph(`${p1}/gubbi-gudu-g4-villa-exterior-anjanapura-bangalore.jpg`, 'Gubbi Gudu — exterior of the G+4 independent villa in Anjanapura, JP Nagar, Bangalore', 'portrait', 'Gubbi Gudu — a G+4 villa in Anjanapura');
const g1Pooja = ph(`${p1}/open-pooja-room-with-carved-wooden-pillars-and-jaali-backdrop.jpg`, `${GG} — open pooja room with carved wooden pillars, turned details and a jaali backdrop`, 'portrait', 'Open pooja room — pillars and turned details');
const g1Island = ph(`${p1}/island-kitchen-with-suspended-chimney-and-wooden-breakfast-counter.jpg`, `${GG} — island kitchen with a suspended chimney clad in wood and a wooden breakfast counter, no loft`, 'portrait', 'Island kitchen with the clad chimney');
const g1KitchenTall = ph(`${p1}/white-kitchen-tall-units-with-built-in-appliances.jpg`, `${GG} — white kitchen tall units with built-in oven and appliances`, 'landscape', 'Kitchen tall units');
const g1Counter = ph(`${p1}/kitchen-breakfast-counter-with-bar-stools-and-pendant-lights.jpg`, `${GG} — kitchen breakfast counter with bar stools and pendant lights`, 'portrait', 'Breakfast counter');
const g1Moon = ph(`${p1}/master-bedroom-with-moon-motif-textured-pop-wall.jpg`, `${GG} — master bedroom with a hand-finished textured POP wall and moon motif`, 'portrait', 'Master bedroom — the moon wall in POP');
const g1Closet = ph(`${p1}/walk-in-closet-with-dark-wardrobes-and-oval-mirror.jpg`, `${GG} — walk-in closet with dark wardrobes and an oval mirror`, 'portrait', 'Walk-in closet');
const g1Parents = ph(`${p1}/parents-bedroom-with-mandala-wallpaper-and-grey-panelling.jpg`, `${GG} — parents’ bedroom with mandala wallpaper and grey wall panelling`, 'portrait', 'Parents’ bedroom');
const g1ParentsTv = ph(`${p1}/parents-bedroom-tv-unit-and-wall-panels.jpg`, `${GG} — parents’ bedroom TV unit with grey wall panels`, 'portrait', 'Parents’ bedroom TV unit');
const g1Kids = ph(`${p1}/kids-room-wooden-loft-bunk-bed-with-staircase-storage.jpg`, `${GG} — kids’ room with a wooden loft bunk bed and staircase storage`, 'landscape', 'Kids’ room — loft bunk bed');
const g1KidsRoom = ph(`${p1}/kids-room-with-loft-bed-and-wall-shelves.jpg`, `${GG} — kids’ room in brown and white with loft bed and wall shelves`, 'landscape', 'Kids’ room');
const g1Shoe = ph(`${p1}/tall-shoe-cabinet-with-backlit-jaali-shutters.jpg`, `${GG} — tall shoe cabinet with backlit jaali shutters by the staircase`, 'portrait', 'Backlit jaali shoe cabinet');
const g1Stair = ph(`${p1}/staircase-with-world-map-wall-and-indoor-garden.jpg`, `${GG} — open-riser staircase with a world-map wall and an indoor garden`, 'portrait', 'Staircase and indoor garden');
const g1StairTop = ph(`${p1}/open-riser-staircase-with-indoor-garden-courtyard-top-view.jpg`, `${GG} — internal courtyard and indoor garden seen from the staircase`, 'portrait', 'The internal courtyard from above');
const g1Powder = ph(`${p1}/powder-room-with-geometric-tiles-and-wall-hung-vanity.jpg`, `${GG} — powder room with geometric tiles and a wall-hung vanity`, 'portrait', 'Powder room');
const g1Bath = ph(`${p1}/terrazzo-bathroom-with-glass-shower-enclosure.jpg`, `${GG} — terrazzo bathroom with a glass shower enclosure`, 'portrait', 'Terrazzo bathroom');

// Gubbi Gudu — 3D renders (interior design: Ms. Navyashree & Ms. Sandhya Jamadagni)
const r1 = (file: string, alt: string, caption: string): ProjectPhoto => ph(`${p1c}/${file}`, `3D render — ${alt}, ${GG}`, 'landscape', caption);

const PROJECT_1: ProjectPhotoSet = {
  // Wide living-room photo leads (full-width banner); the tall one sits in the story
  hero: g1Living,
  gallery: [g1Exterior, g1Pooja, g1Island, g1Moon, g1Kids],
  moreImages: [g1LivingTall, g1TvWide, g1Curtains, g1Closet, g1Parents, g1Shoe, g1Stair, g1StairTop, g1KitchenTall, g1Counter, g1ParentsTv, g1KidsRoom, g1Powder, g1Bath],
  comparisons: [
    { label: 'Living Room — TV Wall', render: r1('living-room-walnut-tv-unit-3d-render.jpg', 'walnut TV unit and fluted wall', '3D Render — TV wall'), actual: { ...g1TvWide, caption: 'As built — TV wall' } },
    { label: 'Living Room', render: r1('living-room-with-full-height-curtains-3d-render.jpg', 'living room with full-height curtains', '3D Render — living room'), actual: { ...g1Curtains, caption: 'As built — living room' } },
    { label: 'Open Pooja Room', render: r1('open-pooja-room-with-pillars-3d-render.jpg', 'open pooja room with pillars and jaali', '3D Render — pooja room'), actual: { ...g1Pooja, caption: 'As built — pooja room' } },
    { label: 'Island Kitchen', render: r1('island-kitchen-with-suspended-chimney-3d-render.jpg', 'island kitchen with a suspended chimney', '3D Render — island kitchen'), actual: { ...g1Island, caption: 'As built — island kitchen' } },
    { label: 'Kitchen Tall Units', render: r1('white-kitchen-tall-units-3d-render.jpg', 'white kitchen tall units', '3D Render — tall units'), actual: { ...g1KitchenTall, caption: 'As built — tall units' } },
    { label: 'Breakfast Counter', render: r1('kitchen-breakfast-counter-3d-render.jpg', 'kitchen breakfast counter', '3D Render — breakfast counter'), actual: { ...g1Counter, caption: 'As built — breakfast counter' } },
    { label: 'Staircase & Indoor Garden', render: r1('staircase-with-world-map-wall-and-indoor-garden-3d-render.jpg', 'staircase with world-map wall and indoor garden', '3D Render — staircase'), actual: { ...g1Stair, caption: 'As built — staircase' } },
    { label: 'Jaali Shoe Cabinet', render: r1('tall-shoe-cabinet-with-jaali-shutters-by-staircase-3d-render.jpg', 'tall shoe cabinet with jaali shutters', '3D Render — shoe cabinet'), actual: { ...g1Shoe, caption: 'As built — shoe cabinet' } },
    { label: 'Master Bedroom — Moon Wall', render: r1('master-bedroom-moon-wall-3d-render.jpg', 'master bedroom with moon wall', '3D Render — moon wall'), actual: { ...g1Moon, caption: 'As built — moon wall' } },
    { label: 'Walk-in Closet', render: r1('walk-in-closet-3d-render.jpg', 'walk-in closet', '3D Render — walk-in closet'), actual: { ...g1Closet, caption: 'As built — walk-in closet' } },
    { label: 'Parents’ Bedroom', render: r1('parents-bedroom-mandala-wall-3d-render.jpg', 'parents’ bedroom with mandala wall', '3D Render — parents’ bedroom'), actual: { ...g1Parents, caption: 'As built — parents’ bedroom' } },
    { label: 'Parents’ Bedroom TV Unit', render: r1('parents-bedroom-tv-unit-3d-render.jpg', 'parents’ bedroom TV unit', '3D Render — TV unit'), actual: { ...g1ParentsTv, caption: 'As built — TV unit' } },
    { label: 'Kids’ Room', render: r1('kids-room-loft-bunk-bed-3d-render.jpg', 'kids’ room with loft bunk bed', '3D Render — kids’ room'), actual: { ...g1Kids, caption: 'As built — kids’ room' } },
    { label: 'Powder Room', render: r1('powder-room-geometric-tiles-3d-render.jpg', 'powder room with geometric tiles', '3D Render — powder room'), actual: { ...g1Powder, caption: 'As built — powder room' } },
    { label: 'Terrazzo Bathroom', render: r1('terrazzo-bathroom-3d-render.jpg', 'terrazzo bathroom', '3D Render — bathroom'), actual: { ...g1Bath, caption: 'As built — bathroom' } },
  ],
  beforeAfter: [],
};

// ─── PROJECT 3 ───────────────────────────────────────────────────────────────
// Saral Mane — Mr. Ashwin's G+2 house, Kanakapura Road (design: Ar. Ujwal Prabhu N, Nord Studio)

const p3 = dir('project-3', 'gallery');
const p3c = dir('project-3', '3d-vs-actual');
const B3 = 'Saral Mane, a G+2 house on Kanakapura Road, Bangalore';

const a3Kitchen = ph(`${p3}/sage-green-l-shaped-modular-kitchen-with-ivory-wall-cabinets.jpg`, `Sage-green L-shaped modular kitchen with ivory wall cabinets and lit open shelves — ${B3}`, 'portrait', 'Kitchen — sage green and ivory');
const a3KitchenSide = ph(`${p3}/sage-green-modular-kitchen-side-view-with-profile-lighting.jpg`, `Sage-green modular kitchen with profile lighting under the base units — ${B3}`, 'portrait', 'Kitchen — side view');
const a3Tv = ph(`${p3}/tv-unit-with-fluted-wood-panel-and-marble-frame.jpg`, `TV unit with a fluted wood panel, marble-finish frame and open display shelves — ${B3}`, 'landscape', 'Living room — TV unit');
const a3Wardrobe = ph(`${p3}/master-bedroom-wardrobe-with-walnut-band.jpg`, `Master bedroom wardrobe in ivory with a walnut band — ${B3}`, 'portrait', 'Master bedroom — wardrobe');
const a3Bed = ph(`${p3}/master-bedroom-with-walnut-headboard-panel-and-floating-bed.jpg`, `Master bedroom with a walnut ceiling panel and floating bed platform — ${B3}`, 'portrait', 'Master bedroom');
const a3Dresser = ph(`${p3}/master-bedroom-dresser-with-lit-mirror-niche.jpg`, `Master bedroom dresser in a lit walnut mirror niche between wardrobes — ${B3}`, 'portrait', 'Master bedroom — dresser niche');
const a3Seat = ph(`${p3}/master-bedroom-window-seat-with-storage.jpg`, `Master bedroom window seat with storage drawers — ${B3}`, 'portrait', 'Window seat with storage');
const a3Parents = ph(`${p3}/parents-bedroom-stone-finish-wall-panel-with-upholstered-headboard.jpg`, `Parents’ bedroom with a stone-finish wall panel and upholstered headboard — ${B3}`, 'portrait', 'Parents’ bedroom — wall panel');
const a3ParentsSide = ph(`${p3}/parents-bedroom-wall-panel-side-view.jpg`, `Parents’ bedroom wall panel side view — ${B3}`, 'portrait', 'Parents’ bedroom');
const a3Sliding = ph(`${p3}/parents-bedroom-taupe-and-white-sliding-wardrobe.jpg`, `Parents’ bedroom sliding wardrobe in taupe and white — ${B3}`, 'portrait', 'Parents’ bedroom — sliding wardrobe');
const a3Kids = ph(`${p3}/kids-bedroom-beige-wardrobe-with-lofts.jpg`, `Kids’ bedroom wardrobe in beige with lofts — ${B3}`, 'portrait', 'Kids’ bedroom — wardrobe');
const r3 = (file: string, alt: string, caption: string, orientation: ProjectPhoto['orientation'] = 'landscape'): ProjectPhoto => ph(`${p3c}/${file}`, `3D render — ${alt} — ${B3}`, orientation, caption);

const PROJECT_3: ProjectPhotoSet = {
  hero: a3Kitchen,
  gallery: [a3Tv, a3Wardrobe, a3Dresser, a3Parents, a3KitchenSide],
  moreImages: [a3Bed, a3Seat, a3ParentsSide, a3Sliding, a3Kids],
  comparisons: [
    { label: 'Kitchen — Front', render: r3('sage-green-modular-kitchen-front-view-3d-render.jpg', 'sage-green L-shaped kitchen, front view', '3D Render — kitchen'), actual: { ...a3Kitchen, caption: 'As built — kitchen' } },
    { label: 'Kitchen — Side', render: r3('sage-green-modular-kitchen-side-view-3d-render.jpg', 'sage-green kitchen, side view', '3D Render — kitchen side', 'portrait'), actual: { ...a3KitchenSide, caption: 'As built — kitchen side' } },
    { label: 'TV Unit', render: r3('tv-unit-with-fluted-wood-panel-3d-render.jpg', 'TV unit with fluted wood panel', '3D Render — TV unit', 'square'), actual: { ...a3Tv, caption: 'As built — TV unit' } },
    { label: 'Master Bedroom Wardrobe', render: r3('master-bedroom-wardrobe-with-walnut-band-3d-render.jpg', 'master wardrobe with walnut band', '3D Render — wardrobe', 'portrait'), actual: { ...a3Wardrobe, caption: 'As built — wardrobe' } },
    { label: 'Dresser Niche', render: r3('master-bedroom-dresser-and-study-corner-3d-render.jpg', 'master bedroom dresser and study corner', '3D Render — dresser', 'portrait'), actual: { ...a3Dresser, caption: 'As built — dresser' } },
    { label: 'Parents’ Bedroom Wall', render: r3('parents-bedroom-stone-wall-panel-3d-render.jpg', 'parents’ bedroom stone wall panel', '3D Render — wall panel'), actual: { ...a3Parents, caption: 'As built — wall panel' } },
    { label: 'Sliding Wardrobe', render: r3('parents-bedroom-sliding-wardrobe-3d-render.jpg', 'parents’ bedroom sliding wardrobe', '3D Render — sliding wardrobe'), actual: { ...a3Sliding, caption: 'As built — sliding wardrobe' } },
    { label: 'Kids’ Wardrobe', render: r3('kids-bedroom-wardrobe-3d-render.jpg', 'kids’ bedroom wardrobe', '3D Render — kids’ wardrobe', 'square'), actual: { ...a3Kids, caption: 'As built — kids’ wardrobe' } },
  ],
  beforeAfter: [],
};

// ─── PROJECT 2 ───────────────────────────────────────────────────────────────

const p2 = dir('project-2', 'gallery');
const p2c = dir('project-2', '3d-vs-actual');

const p2Kitchen: ProjectPhoto = {
  src: `${p2}/l-shaped-modular-kitchen-with-wooden-lofts-and-lit-glass-shutters.jpg`,
  alt: 'L-shaped modular kitchen in Rasha, an apartment at SJR Parkway Homes, Hosa Road, Electronic City, with wooden lofts, lit glass-shutter wall cabinets, black granite counter and warm under-cabinet lighting',
  orientation: 'landscape',
  caption: 'Kitchen — wooden lofts and lit glass shutters',
};
const p2MasterWardrobe: ProjectPhoto = {
  src: `${p2}/beige-wardrobe-with-black-glass-unit-and-fluted-panel-master-bedroom.jpg`,
  alt: 'Master bedroom wardrobe in beige laminate with a black-framed glass centre unit, lofts and a fluted wood side panel',
  orientation: 'landscape',
  caption: 'Master bedroom — wardrobe with glass centre unit',
};
const p2GlassWardrobe: ProjectPhoto = {
  src: `${p2}/black-frame-glass-shutter-wardrobe-with-led-lighting.jpg`,
  alt: 'Black-framed glass shutter wardrobe with warm LED profile lighting on every shelf and white lofts above',
  orientation: 'portrait',
  caption: 'Master bedroom — lit glass-shutter wardrobe',
};
const p2Study: ProjectPhoto = {
  src: `${p2}/l-shaped-study-table-with-arched-pooja-niche.jpg`,
  alt: 'L-shaped study table under a window with a tall pooja unit featuring an arched niche and floral laminate drawers',
  orientation: 'square',
  caption: 'Study — L-shaped desk with pooja niche',
};
const p2ShoeRack: ProjectPhoto = {
  src: `${p2}/wooden-shoe-rack-with-lit-open-niche-and-grey-shutters.jpg`,
  alt: 'Foyer shoe rack in wood and grey laminate with a lit open niche and profile-lit floating base',
  orientation: 'square',
  caption: 'Foyer — shoe rack with lit niche',
};
const p2FloralWardrobe: ProjectPhoto = {
  src: `${p2}/floral-laminate-wardrobe-with-lofts-study-room.jpg`,
  alt: 'Floral laminate wardrobe with lofts framing an open alcove, in a study room',
  orientation: 'portrait',
  caption: 'Study room — floral laminate wardrobe',
};

const PROJECT_2: ProjectPhotoSet = {
  hero: p2Kitchen,
  gallery: [
    {
      src: `${p2}/beige-wardrobe-built-around-window-with-lofts-guest-bedroom.jpg`,
      alt: 'Guest bedroom wardrobe in beige laminate built around the window, with a window-seat ledge, lofts and small wooden knobs',
      orientation: 'landscape',
      caption: 'Guest bedroom — wardrobe built around the window',
    },
    p2GlassWardrobe,
    {
      src: `${p2}/pichwai-style-pooja-unit-with-arched-niche-and-drawers.jpg`,
      alt: 'Pooja unit with a Pichwai-style tree and cow artwork in an arched niche, above floral laminate drawers',
      orientation: 'portrait',
      caption: 'Pooja unit — Pichwai-style artwork',
    },
    p2MasterWardrobe,
    p2Study,
  ],
  moreImages: [
    p2ShoeRack,
    p2FloralWardrobe,
    {
      src: `${p2}/cream-wall-panel-with-profile-light-strips-and-wall-sconces.jpg`,
      alt: 'Living area wall panel in cream with two vertical profile light strips, woven wall sconces and a cove-lit false ceiling',
      orientation: 'portrait',
      caption: 'Living area — wall panel with light strips',
    },
  ],
  comparisons: [
    {
      label: 'Kitchen',
      render: { src: `${p2c}/l-shaped-modular-kitchen-with-glass-shutters-3d-render.jpg`, alt: '3D render of an L-shaped modular kitchen with wooden lofts and glass-shutter wall cabinets', orientation: 'landscape', caption: '3D Render — kitchen' },
      actual: { ...p2Kitchen, caption: 'As built — kitchen' },
    },
    {
      label: 'Master Bedroom Wardrobe',
      render: { src: `${p2c}/beige-wardrobe-with-black-glass-unit-master-bedroom-3d-render.jpg`, alt: '3D render of a beige master bedroom wardrobe with a black glass centre unit', orientation: 'landscape', caption: '3D Render — master wardrobe' },
      actual: { ...p2MasterWardrobe, caption: 'As built — master wardrobe' },
    },
    {
      label: 'Glass-Shutter Wardrobe',
      render: { src: `${p2c}/black-glass-shutter-wardrobe-master-bedroom-3d-render.jpg`, alt: '3D render of a black glass-shutter wardrobe beside the bed in a master bedroom', orientation: 'landscape', caption: '3D Render — glass-shutter wardrobe' },
      actual: { ...p2GlassWardrobe, caption: 'As built — glass-shutter wardrobe' },
    },
    {
      label: 'Study & Pooja Unit',
      render: { src: `${p2c}/pooja-mandir-with-l-shaped-study-table-3d-render.jpg`, alt: '3D render of a pooja mandir with an arched niche beside an L-shaped study table and open shelves', orientation: 'landscape', caption: '3D Render — study and pooja unit' },
      actual: { ...p2Study, caption: 'As built — study and pooja unit' },
    },
    {
      label: 'Shoe Rack',
      render: { src: `${p2c}/shoe-rack-with-open-storage-niche-3d-render.jpg`, alt: '3D render of a tall shoe rack in wood and grey with an open storage niche', orientation: 'portrait', caption: '3D Render — shoe rack' },
      actual: { ...p2ShoeRack, caption: 'As built — shoe rack' },
    },
    {
      label: 'Floral Wardrobe',
      render: { src: `${p2c}/floral-laminate-wardrobe-with-lofts-3d-render.jpg`, alt: '3D render of a floral laminate wardrobe with lofts around an open alcove', orientation: 'landscape', caption: '3D Render — floral wardrobe' },
      actual: { ...p2FloralWardrobe, caption: 'As built — floral wardrobe' },
    },
  ],
  beforeAfter: [],
};

// ─── PROJECT 4 ───────────────────────────────────────────────────────────────

const p4 = dir('project-4', 'gallery');
const p4c = dir('project-4', '3d-vs-actual');
const p4b = dir('project-4', 'before-after');

const p4Kitchen: ProjectPhoto = {
  src: `${p4}/teal-l-shaped-modular-kitchen-with-lit-glass-crockery-unit.jpg`,
  alt: 'L-shaped modular kitchen in Blue on Mars, a 3 BHK at The Mars Planet, Banashankari with glossy teal base cabinets, wood-finish wall units, patterned backsplash and a lit glass-front crockery unit',
  orientation: 'landscape',
  caption: 'Kitchen — teal base units and lit crockery unit',
};
const p4MasterWardrobe: ProjectPhoto = {
  src: `${p4}/white-high-gloss-wardrobe-with-lit-glass-shutters-master-bedroom.jpg`,
  alt: 'Master bedroom wardrobe with fluted-glass profile shutters, wooden external drawers and handle-free push-to-open lofts',
  orientation: 'landscape',
  caption: 'Master bedroom — long wardrobe broken up with glass and wood',
};
const p4TvWall: ProjectPhoto = {
  src: `${p4}/wooden-tv-wall-panel-with-floating-stone-console.jpg`,
  alt: 'Full-height wooden TV wall panel with a floating grey stone console, profile lighting and a lit glass display niche',
  orientation: 'portrait',
  caption: 'Living room — full-wall TV panel',
};
const p4Foyer: ProjectPhoto = {
  src: `${p4}/foyer-seating-unit-with-arched-metal-partition-and-display-shelves.jpg`,
  alt: 'Foyer seating bench with an arched metal-frame partition, lit display shelves and a view into the teal kitchen',
  orientation: 'portrait',
  caption: 'Entrance — foyer seating with arched metal partition',
};
const p4StudyPortrait: ProjectPhoto = {
  src: `${p4}/window-study-table-with-patterned-base-and-walnut-bookshelf.jpg`,
  alt: 'Window study table with a white top on a patterned base, beside a walnut bookshelf with lit open shelves',
  orientation: 'portrait',
  caption: 'Study bedroom — window study table',
};

const PROJECT_4: ProjectPhotoSet = {
  hero: p4Kitchen,
  gallery: [
    p4MasterWardrobe,
    p4TvWall,
    {
      src: `${p4}/pooja-unit-with-folding-doors-and-blue-drawers.jpg`,
      alt: 'Pooja unit with folding doors opened to reveal a lit shrine, steps and teal drawers, built into the kitchen joinery',
      orientation: 'portrait',
      caption: 'Pooja unit — folding doors and teal drawers',
    },
    {
      src: `${p4}/window-study-table-with-walnut-bookshelf-wide-view.jpg`,
      alt: 'Study bedroom study table along the window with a walnut bookshelf, lit shelves and a city view',
      orientation: 'landscape',
      caption: 'Study bedroom — study table by the window',
    },
    p4Foyer,
  ],
  moreImages: [
    {
      src: `${p4}/tv-unit-with-lit-glass-display-tower-and-stone-console.jpg`,
      alt: 'TV unit with a tall lit glass display tower, wooden wall panel and a grey stone floating console',
      orientation: 'portrait',
      caption: 'Living room — lit glass display tower',
    },
    {
      src: `${p4}/wooden-tv-wall-panel-with-lit-display-niche.jpg`,
      alt: 'Wooden TV wall panel with vertical grooves, a lit display niche and a stone console, front view',
      orientation: 'portrait',
      caption: 'Living room — TV wall front view',
    },
    {
      src: `${p4}/walnut-laminate-wardrobe-with-fluted-drawers-guest-bedroom.jpg`,
      alt: 'Study bedroom wardrobe in walnut laminate with fluted lower drawers and oval wooden handles',
      orientation: 'portrait',
      caption: 'Study bedroom — walnut wardrobe',
    },
    {
      src: `${p4}/walnut-tall-storage-unit-with-fluted-drawers-guest-bedroom.jpg`,
      alt: 'Tall walnut storage unit with fluted drawers beside a window with a patterned sill',
      orientation: 'portrait',
      caption: 'Study bedroom — tall storage unit',
    },
    p4StudyPortrait,
  ],
  comparisons: [
    {
      label: 'Kitchen',
      render: { src: `${p4c}/teal-l-shaped-modular-kitchen-3d-render.jpg`, alt: '3D render of an L-shaped modular kitchen with blue base cabinets and wood-finish wall units', orientation: 'landscape', caption: '3D Render — kitchen' },
      actual: { ...p4Kitchen, caption: 'As built — kitchen' },
    },
    {
      label: 'Master Bedroom',
      render: { src: `${p4c}/master-bedroom-wardrobe-with-glass-shutters-3d-render.jpg`, alt: '3D render of a master bedroom wardrobe with frosted glass shutters and white doors', orientation: 'landscape', caption: '3D Render — master wardrobe' },
      actual: { src: `${p4c}/master-bedroom-wardrobe-with-glass-shutters-as-built.jpg`, alt: 'Completed master bedroom wardrobe with fluted-glass profile shutters and push-to-open lofts', orientation: 'landscape', caption: 'As built — master wardrobe' },
    },
    {
      label: 'Living — TV Unit',
      render: { src: `${p4c}/wooden-tv-wall-panel-with-glass-display-unit-3d-render.jpg`, alt: '3D render of a wooden TV wall panel with a tall glass display unit', orientation: 'landscape', caption: '3D Render — TV unit' },
      actual: { src: `${p4c}/wooden-tv-wall-panel-with-glass-display-unit-as-built.jpg`, alt: 'Completed wooden TV wall panel with a lit glass display niche and stone console', orientation: 'portrait', caption: 'As built — TV unit' },
    },
    {
      label: 'Study Bedroom',
      render: { src: `${p4c}/guest-bedroom-wardrobe-and-study-table-3d-render.jpg`, alt: '3D render of a study bedroom wardrobe with arched panels and a study table by the window', orientation: 'landscape', caption: '3D Render — study bedroom' },
      actual: { src: `${p4c}/guest-bedroom-walnut-wardrobe-as-built.jpg`, alt: 'Completed study bedroom wardrobe in walnut laminate with fluted drawers', orientation: 'portrait', caption: 'As built — study bedroom wardrobe' },
    },
    {
      label: 'Foyer',
      render: { src: `${p4c}/foyer-seating-unit-with-metal-partition-3d-render.jpg`, alt: '3D render of a foyer seating unit with a metal partition and open shelves', orientation: 'landscape', caption: '3D Render — foyer' },
      actual: { src: `${p4c}/foyer-seating-unit-with-metal-partition-as-built.jpg`, alt: 'Completed foyer seating unit with an arched metal partition and lit shelves', orientation: 'portrait', caption: 'As built — foyer' },
    },
  ],
  beforeAfter: [
    {
      label: 'Kitchen',
      before: { src: `${p4b}/kitchen-before-interior-work.jpg`, alt: 'Kitchen before interior work — bare walls, exposed plumbing and loose wiring', orientation: 'portrait', caption: 'Before' },
      after: { ...p4Kitchen, caption: 'After' },
    },
    {
      label: 'Living Room',
      before: { src: `${p4b}/living-room-before-interior-work.jpg`, alt: 'Living room before interior work — empty room with bare walls and balcony door', orientation: 'portrait', caption: 'Before' },
      after: { ...p4TvWall, caption: 'After' },
    },
    {
      label: 'Master Bedroom',
      before: { src: `${p4b}/master-bedroom-before-interior-work.jpg`, alt: 'Master bedroom before interior work — empty room with exposed wiring', orientation: 'portrait', caption: 'Before' },
      after: { ...p4MasterWardrobe, caption: 'After' },
    },
    {
      label: 'Study Bedroom',
      before: { src: `${p4b}/guest-bedroom-before-interior-work.jpg`, alt: 'Study bedroom before interior work — empty room with bare walls', orientation: 'portrait', caption: 'Before' },
      after: { ...p4StudyPortrait, caption: 'After' },
    },
    {
      label: 'Dining & Foyer',
      before: { src: `${p4b}/dining-area-before-interior-work.jpg`, alt: 'Dining area before interior work — empty space with bare walls', orientation: 'portrait', caption: 'Before' },
      after: { ...p4Foyer, caption: 'After' },
    },
  ],
};

// ─── PROJECT 5 ───────────────────────────────────────────────────────────────

const p5 = dir('project-5', 'gallery');
const p5c = dir('project-5', '3d-vs-actual');

const p5BlueKitchen: ProjectPhoto = {
  src: `${p5}/green-l-shaped-modular-kitchen-with-wooden-lofts.jpg`,
  alt: 'The family’s green modular kitchen in The Green Storey, a G+3 independent house in Bannerghatta, Bangalore — green base cabinets, wooden lofts, black counter and lit open shelves',
  orientation: 'landscape',
  caption: 'The family kitchen — green with wooden lofts',
};
const p5TealKitchen: ProjectPhoto = {
  src: `${p5}/dark-blue-l-shaped-modular-kitchen-with-white-lofts.jpg`,
  alt: 'Rental-floor kitchen in The Green Storey, Bannerghatta — dark blue base cabinets, white lofts and a marble-look counter',
  orientation: 'landscape',
  caption: 'Rental floor — dark blue kitchen with white lofts',
};
const p5KidsRoom: ProjectPhoto = {
  src: `${p5}/kids-bedroom-with-teal-study-unit-and-botanical-wallpaper.jpg`,
  alt: 'Kids bedroom with a wall-mounted teal study unit and lit niche, upholstered bed and botanical wallpaper',
  orientation: 'landscape',
  caption: 'Kids bedroom — teal study unit',
};
const p5KidsWardrobe: ProjectPhoto = {
  src: `${p5}/wooden-wardrobe-with-lit-glass-shutters-and-dresser-kids-bedroom.jpg`,
  alt: 'Kids bedroom wardrobe in wood laminate with lit black-framed glass shutters and an attached dresser',
  orientation: 'landscape',
  caption: 'Kids bedroom — wooden wardrobe with lit glass shutters',
};
const p5MasterWardrobe: ProjectPhoto = {
  src: `${p5}/beige-wardrobe-with-lit-glass-shutters-and-dresser-master-bedroom.jpg`,
  alt: 'Master bedroom wardrobe in beige laminate with a lit black glass centre unit, long bar handles and a dresser',
  orientation: 'landscape',
  caption: 'Master bedroom — beige wardrobe with lit glass centre',
};
const p5TvPanel: ProjectPhoto = {
  src: `${p5}/wooden-tv-panel-with-white-base-cabinets.jpg`,
  alt: 'Wooden TV wall panel above white base cabinets on a wooden plinth',
  orientation: 'portrait',
  caption: 'Rental floor — floor-standing TV unit',
};
const p5TvWall: ProjectPhoto = {
  src: `${p5}/fluted-marble-tv-wall-with-lit-glass-display-unit.jpg`,
  alt: 'TV wall with fluted marble-look panels, a wooden slatted side panel, low white cabinets and a lit glass display unit',
  orientation: 'landscape',
  caption: 'Living room — fluted TV wall with lit display',
};
const p5SlidingWhite: ProjectPhoto = {
  src: `${p5}/white-sliding-door-wardrobe-with-lofts.jpg`,
  alt: 'White sliding-door wardrobe with wooden frame and lofts above, beside a wooden bedroom door',
  orientation: 'landscape',
  caption: 'Rental floor — white sliding wardrobe with a touch of brown',
};

const PROJECT_5: ProjectPhotoSet = {
  hero: p5BlueKitchen,
  gallery: [p5KidsRoom, p5TvPanel, p5MasterWardrobe, p5KidsWardrobe, p5TvWall],
  moreImages: [
    {
      src: `${p5}/wooden-slatted-partition-with-display-shelves-living-dining.jpg`,
      alt: 'Wooden slatted partition with display shelves between the living and dining areas, with a cove-lit ceiling',
      orientation: 'landscape',
      caption: 'Living & dining — slatted partition',
    },
    p5TealKitchen,
    p5SlidingWhite,
    {
      src: `${p5}/taupe-sliding-door-wardrobe-with-lofts.jpg`,
      alt: 'Taupe sliding-door wardrobe with matching lofts above',
      orientation: 'landscape',
      caption: 'Rental floor — taupe sliding wardrobe',
    },
  ],
  comparisons: [
    {
      label: 'Kitchen',
      render: { src: `${p5c}/green-l-shaped-modular-kitchen-with-wooden-lofts-3d-render.jpg`, alt: '3D render of the family’s green L-shaped kitchen with wooden lofts', orientation: 'landscape', caption: '3D Render — kitchen' },
      actual: { ...p5BlueKitchen, caption: 'As built — kitchen' },
    },
    {
      label: 'Kids Bedroom',
      render: { src: `${p5c}/kids-bedroom-with-teal-study-unit-3d-render.jpg`, alt: '3D render of a kids bedroom with a teal study unit and bed', orientation: 'landscape', caption: '3D Render — kids bedroom' },
      actual: { ...p5KidsRoom, caption: 'As built — kids bedroom' },
    },
    {
      label: 'Kids Wardrobe',
      render: { src: `${p5c}/wooden-wardrobe-with-frosted-glass-shutters-kids-bedroom-3d-render.jpg`, alt: '3D render of a wooden wardrobe with frosted glass shutters in a kids bedroom', orientation: 'landscape', caption: '3D Render — kids wardrobe' },
      actual: { ...p5KidsWardrobe, caption: 'As built — kids wardrobe' },
    },
    {
      label: 'Master Bedroom Wardrobe',
      render: { src: `${p5c}/white-wardrobe-with-glass-walk-in-shutters-master-bedroom-3d-render.jpg`, alt: '3D render of a white master bedroom wardrobe with glass shutters', orientation: 'landscape', caption: '3D Render — master wardrobe' },
      actual: { ...p5MasterWardrobe, caption: 'As built — master wardrobe' },
    },
    {
      label: 'TV Unit',
      render: { src: `${p5c}/wooden-tv-panel-with-white-base-cabinets-3d-render.jpg`, alt: '3D render of a wooden TV panel with white base cabinets', orientation: 'landscape', caption: '3D Render — TV unit' },
      actual: { ...p5TvPanel, caption: 'As built — TV unit' },
    },
    {
      label: 'Living Room TV Wall',
      render: { src: `${p5c}/living-room-tv-wall-with-glass-display-unit-3d-render.jpg`, alt: '3D render of a living room TV wall with panels and a glass display unit', orientation: 'square', caption: '3D Render — TV wall' },
      actual: { ...p5TvWall, caption: 'As built — TV wall' },
    },
    {
      label: 'Sliding Wardrobe',
      render: { src: `${p5c}/white-sliding-door-wardrobe-with-lofts-3d-render.jpg`, alt: '3D render of a white sliding-door wardrobe with lofts', orientation: 'landscape', caption: '3D Render — sliding wardrobe' },
      actual: { ...p5SlidingWhite, caption: 'As built — sliding wardrobe' },
    },
    {
      label: 'Rental Floor Kitchen',
      render: { src: `${p5c}/dark-blue-l-shaped-modular-kitchen-3d-render.jpg`, alt: '3D render of the rental floor’s dark blue L-shaped kitchen with white lofts', orientation: 'landscape', caption: '3D Render — rental kitchen' },
      actual: { ...p5TealKitchen, caption: 'As built — rental kitchen' },
    },
  ],
  beforeAfter: [],
};

// ─── PROJECT 6 ───────────────────────────────────────────────────────────────

const p6 = dir('project-6', 'gallery');
const p6c = dir('project-6', '3d-vs-actual');

const p6Crockery: ProjectPhoto = {
  src: `${p6}/sage-green-crockery-unit-with-lit-niche-and-black-granite-top.jpg`,
  alt: 'Sage-green crockery unit with black granite top, lit open niche and wooden ceiling rafters, in Sage & Sunshine, an apartment on Sarjapur Road, Bangalore',
  orientation: 'portrait',
  caption: 'Dining — sage-green crockery unit',
};
const p6TallUnit: ProjectPhoto = {
  src: `${p6}/sage-green-tall-kitchen-unit-with-breakfast-counter.jpg`,
  alt: 'Sage-green tall kitchen unit beside a breakfast counter with black granite top, under wooden ceiling rafters',
  orientation: 'portrait',
  caption: 'Kitchen — sage tall unit and breakfast counter',
};
const p6Partition: ProjectPhoto = {
  src: `${p6}/sage-fluted-partition-with-lit-display-shelves-and-console.jpg`,
  alt: 'Sage fluted partition with a lit glass display unit and a slatted wooden console between the living and dining areas',
  orientation: 'landscape',
  caption: 'Living & dining — fluted partition with lit display',
};
const p6PartitionView2: ProjectPhoto = {
  src: `${p6}/wooden-fluted-partition-with-lit-display-unit-living-dining.jpg`,
  alt: 'View through the living-dining space with fluted partition, lit display unit and slatted wooden console',
  orientation: 'portrait',
  caption: 'Living & dining — view along the partition',
};
const p6Terrazzo: ProjectPhoto = {
  src: `${p6}/terrazzo-and-bronze-laminate-wardrobe-with-lofts-elderly-bedroom.jpg`,
  alt: 'Bedroom wardrobe in brown laminate with a Dorby Mica digital print terrazzo-pattern centre panel and lofts',
  orientation: 'landscape',
  caption: 'Parents’ bedroom — terrazzo-panel wardrobe',
};
const p6Mustard: ProjectPhoto = {
  src: `${p6}/mustard-wardrobe-with-patterned-laminate-centre-guest-bedroom.jpg`,
  alt: 'Bedroom wardrobe in sunny yellow laminate with a Dorby Mica digital print laminate centre panel and lofts',
  orientation: 'landscape',
  caption: 'Guest bedroom — mustard wardrobe',
};
const p6Sliding: ProjectPhoto = {
  src: `${p6}/white-and-grey-sliding-wardrobe-with-dresser-master-bedroom.jpg`,
  alt: 'Master bedroom sliding wardrobe in white and grey with wooden lofts, a dresser and a layered cove-lit ceiling',
  orientation: 'portrait',
  caption: 'Master bedroom — sliding wardrobe with dresser',
};
const p6FeatureWall: ProjectPhoto = {
  src: `${p6}/leaf-texture-feature-wall-with-wooden-frame-living-room.jpg`,
  alt: 'Living room feature wall with a large leaf-texture panel framed in wood, with cove lighting',
  orientation: 'landscape',
  caption: 'Living room — leaf-texture feature wall',
};

const PROJECT_6: ProjectPhotoSet = {
  hero: {
    src: `${p6}/open-living-dining-with-sage-crockery-unit-and-wooden-ceiling-rafters.jpg`,
    alt: 'Open living and dining space in Sage & Sunshine, a Sarjapur Road apartment, with a sage-green crockery unit, wooden ceiling rafters and a jaali panel',
    orientation: 'landscape',
  },
  gallery: [
    p6FeatureWall,
    {
      src: `${p6}/sage-green-l-shaped-modular-kitchen-with-wooden-wall-cabinets.jpg`,
      alt: 'L-shaped modular kitchen with sage-green base cabinets, wood-finish wall cabinets, lit glass shutters and a black counter',
      orientation: 'portrait',
      caption: 'Kitchen — sage-green L-shaped kitchen',
    },
    p6Crockery,
    p6Partition,
    p6Terrazzo,
  ],
  moreImages: [p6PartitionView2, p6TallUnit, p6Sliding, p6Mustard],
  comparisons: [
    {
      label: 'Dining — Crockery Unit',
      render: { src: `${p6c}/sage-green-crockery-unit-with-dining-table-3d-render.jpg`, alt: '3D render of a sage-green crockery unit with a dining table and wooden ceiling rafters', orientation: 'landscape', caption: '3D Render — crockery unit' },
      actual: { ...p6Crockery, caption: 'As built — crockery unit' },
    },
    {
      label: 'Kitchen — Tall Unit',
      render: { src: `${p6c}/sage-green-tall-kitchen-unit-with-breakfast-counter-3d-render.jpg`, alt: '3D render of a sage-green tall kitchen unit with a breakfast counter', orientation: 'landscape', caption: '3D Render — tall unit' },
      actual: { ...p6TallUnit, caption: 'As built — tall unit' },
    },
    {
      label: 'Living & Dining Partition',
      render: { src: `${p6c}/sage-fluted-partition-with-display-unit-living-dining-3d-render.jpg`, alt: '3D render of a sage fluted partition with display unit between living and dining', orientation: 'landscape', caption: '3D Render — partition' },
      actual: { ...p6Partition, caption: 'As built — partition' },
    },
    {
      label: 'Living & Dining — View 2',
      render: { src: `${p6c}/fluted-partition-and-display-unit-living-dining-view-2-3d-render.jpg`, alt: '3D render of the fluted partition and tall display unit, second view', orientation: 'landscape', caption: '3D Render — second view' },
      actual: { ...p6PartitionView2, caption: 'As built — second view' },
    },
    {
      label: 'Parents’ Bedroom Wardrobe',
      render: { src: `${p6c}/terrazzo-and-bronze-wardrobe-elderly-bedroom-3d-render.jpg`, alt: '3D render of a bronze wardrobe with a terrazzo centre panel in an elderly parents bedroom', orientation: 'landscape', caption: '3D Render — terrazzo wardrobe' },
      actual: { ...p6Terrazzo, caption: 'As built — terrazzo wardrobe' },
    },
    {
      label: 'Guest Bedroom Wardrobe',
      render: { src: `${p6c}/mustard-and-white-wardrobe-with-study-table-guest-bedroom-3d-render.jpg`, alt: '3D render of a mustard and white wardrobe with a study table in a guest bedroom', orientation: 'landscape', caption: '3D Render — guest wardrobe' },
      actual: { ...p6Mustard, caption: 'As built — guest wardrobe' },
    },
    {
      label: 'Master Bedroom',
      render: { src: `${p6c}/sliding-wardrobe-with-dresser-master-bedroom-3d-render.jpg`, alt: '3D render of a master bedroom with a sliding wardrobe and dresser', orientation: 'landscape', caption: '3D Render — master bedroom' },
      actual: { ...p6Sliding, caption: 'As built — master bedroom' },
    },
    {
      label: 'Living Room Feature Wall',
      render: { src: `${p6c}/living-room-feature-wall-with-wallpaper-3d-render.jpg`, alt: '3D render of a living room feature wall with wallpaper and wooden frame', orientation: 'landscape', caption: '3D Render — feature wall' },
      actual: { ...p6FeatureWall, caption: 'As built — feature wall' },
    },
  ],
  beforeAfter: [],
};

/** Finds one of a project's photos by file name, so other pages reuse the same src + alt text. */
export function findProjectPhoto(slug: string, fileName: string): ProjectPhoto | undefined {
  const set = PROJECT_PHOTOS[slug];
  if (!set) return undefined;
  const all = [
    set.hero,
    ...set.gallery,
    ...set.moreImages,
    ...(set.comparisons ?? []).flatMap((c) => [c.render, c.actual]),
    ...(set.beforeAfter ?? []).flatMap((b) => [b.before, b.after]),
  ];
  return all.find((p) => p.src.endsWith(`/${fileName}`));
}

export const PROJECT_PHOTOS: Record<string, ProjectPhotoSet> = {
  // Project 1 = Gubbi Gudu (old address /projects/project-1 redirects here, see next.config.mjs)
  'gubbi-gudu-villa-anjanapura': PROJECT_1,
  'saral-mane-g2-house-kanakapura-road': PROJECT_3, // Saral Mane (old /projects/project-3 redirects)
  'rasha-apartment-hosa-road-electronic-city': PROJECT_2, // Rasha (old /projects/project-2 redirects)
  'blue-on-mars-apartment-banashankari': PROJECT_4, // Blue on Mars (old /projects/project-4 redirects)
  'the-green-storey-g3-house-bannerghatta': PROJECT_5, // The Green Storey (old /projects/project-5 redirects)
  'sage-and-sunshine-apartment-sarjapur': PROJECT_6, // Sage & Sunshine (old /projects/project-6 redirects)
};
